import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";

const ModeContext = createContext(null);

const MODES = {
  VIEW: "view",
  ADD: "add",
  EDIT: "edit",
  COPY: "copy",
};

export const ModeProvider = ({ children }) => {
  const [mode, setModeState] = useState(MODES.VIEW);
  const location = useLocation();

  // Reset mode on route change
  useEffect(() => {
    setModeState(MODES.VIEW);
  }, [location.pathname]);

  const setMode = useCallback((newMode) => {
    if (Object.values(MODES).includes(newMode)) {
      setModeState(newMode);
    } else {
      console.warn(`Invalid mode: ${newMode}. Using default mode.`);
      setModeState(MODES.VIEW);
    }
  }, []);

  const isViewMode = mode === MODES.VIEW;
  const isEditMode = mode === MODES.EDIT;
  const isAddMode = mode === MODES.ADD;
  const isCopyMode = mode === MODES.COPY;

  const value = useMemo(
    () => ({
      mode,
      setMode,
      isViewMode,
      isEditMode,
      isAddMode,
      isCopyMode,
      MODES,
    }),
    [mode, setMode, isViewMode, isEditMode, isAddMode, isCopyMode]
  );

  return (
    <ModeContext.Provider value={value}>
      {children}
    </ModeContext.Provider>
  );
};

export const useMode = () => {
  const context = useContext(ModeContext);
  if (!context) {
    throw new Error("useMode must be used within a ModeProvider");
  }
  return context;
};

export { MODES };
